import { motion } from "framer-motion";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CalendarDays, DollarSign, Clock, ShieldAlert, Swords, Shield, Ban, TreePine,
  GraduationCap, ClipboardList, CalendarRange, Users, Heart, Download,
} from "lucide-react";

const rules = [
  {
    icon: CalendarDays,
    title: "Season Start Date & Schedule",
    body: [
      "The full 2026 season schedule, including playoffs, is now available. View it in the Schedule section above or download the complete PDF below.",
      "We are still finalizing a few league logistics including field availability, umpire staffing, and team formation. Any updates or changes will be shared on this page.",
    ],
    link: { url: "https://customer-assets-7cd3h4nn.emergentagent.net/job_adult-baseball-hub/artifacts/jum1w41i_WABL%20Schedule%20w_%20Playoffs.pdf", label: "Download Full Schedule (PDF)" },
  },
  {
    icon: DollarSign,
    title: "League Dues",
    body: [
      "Yes, there will be a player entry fee to participate in the league. League dues will help cover expenses including:",
    ],
    list: [
      "Two umpires per game",
      "Field rentals",
      "League insurance for players and teams",
      "Game balls",
      "Awards and trophies",
      "Administrative and operating expenses",
    ],
    after: [
      "We are still finalizing costs and therefore do not have an exact player fee at this time. This is a pay-to-play league because these expenses are not free.",
      "There will be no cash prize for the championship team. This league is about playing baseball, competing, and enjoying the game—not playing for a payout.",
    ],
  },
  {
    icon: Clock,
    title: "Game Format",
    list: ["9-inning games", "2 hour, 45 minute time limit", "When time expires, the current inning will be completed"],
  },
  {
    icon: ShieldAlert,
    title: "Player Conduct & Ejections",
    body: ["The league will operate under a 3-strike ejection policy:"],
    list: [
      "1st Ejection: 1-game suspension",
      "2nd Ejection: 3-game suspension",
      "3rd Ejection: Removal from the league",
    ],
  },
  {
    icon: Swords,
    title: "Fighting",
    body: [
      "This league will not tolerate fighting of any kind.",
      "Any player involved in a fight will be ejected and disciplinary action will be reviewed by the league.",
    ],
  },
  {
    icon: Shield,
    title: "Umpire Respect",
    body: [
      "This league will not tolerate abuse of umpires.",
      "No umpire is perfect. Missed calls are part of baseball and always will be. We will do our best to recruit quality officials and address any concerns through the proper channels, but yelling at, cursing at, threatening, or disrespecting umpires will not be tolerated.",
      "Any such behavior will be subject to the 3-strike ejection policy. Without umpires, there is no league.",
    ],
  },
  {
    icon: Ban,
    title: "Alcohol & Drugs",
    body: [
      "No alcohol or illegal drugs will be permitted at games.",
      "Many of our games will be played on school properties and sports complexes. Any player or spectator found using alcohol or drugs at league events will be subject to league discipline under the 3-strike policy.",
    ],
  },
  {
    icon: TreePine,
    title: "Wood Bat League",
    body: ["The WABL is a wood bat league. Only the following will be permitted:"],
    list: ["Wood bats", "Wood-composite bats"],
  },
  {
    icon: GraduationCap,
    title: "Player Eligibility",
    body: [
      "This league was created for former baseball players who want to continue playing the game they love without competing against current collegiate or professional athletes.",
      "Current college and professional players will not be permitted on league rosters. If a current college or professional player is caught playing, that player will be removed from the league for the remainder of the season. If a second player is caught, the manager allowing it to happen and the player will both be removed from the league for the remainder of the season.",
      "Former college and professional players are welcome.",
    ],
  },
  {
    icon: ClipboardList,
    title: "Rosters",
    list: [
      "Maximum roster size: 20 players",
      "Rosters must be submitted prior to Opening Day",
      "No players may be added after the season begins",
    ],
    after: [
      "The only exception will be significant season-ending injuries, and any replacement player must receive league approval.",
    ],
  },
  {
    icon: CalendarRange,
    title: "Game Days",
    body: [
      "Games will be played on Saturdays and Sundays only.",
      "We understand that not every schedule can be accommodated, but we will do our best to create a schedule that works for the majority of players and teams.",
    ],
  },
  {
    icon: Users,
    title: "Family-Friendly Environment",
    body: [
      "One of the core goals of this league is to create a family-friendly atmosphere where wives, children, parents, grandparents, friends, and fans can enjoy the game together.",
      "Anyone whose behavior threatens that atmosphere—on or off the field—will not be welcome in the league.",
    ],
  },
];

const VISION = ["Respect", "Sportsmanship", "Accountability", "Competition", "Community"];

export const Rules = () => {
  return (
    <section id="rules" data-testid="rules-section" className="relative py-24 lg:py-32 bg-[#050505] border-t border-white/10">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="text-[#FF3B30] text-xs font-bold uppercase tracking-[0.3em]">The Fine Print</span>
          <h2 className="text-4xl sm:text-5xl uppercase tracking-tighter mt-4 leading-[0.95]">
            Rules &amp; Regulations
          </h2>
          <p className="mt-6 text-[#8A94A0] text-lg font-medium">
            The foundation of the league. Tap any section to read the details.
          </p>
        </motion.div>

        <Accordion type="single" collapsible className="mt-12 border border-white/10" data-testid="rules-accordion">
          {rules.map((r, i) => {
            const Icon = r.icon;
            return (
              <AccordionItem key={r.title} value={`item-${i}`} className="border-b border-white/10 last:border-b-0 px-5 sm:px-7">
                <AccordionTrigger data-testid={`rule-trigger-${i}`} className="hover:no-underline py-5 text-left">
                  <span className="flex items-center gap-4">
                    <Icon className="text-[#FF3B30] shrink-0" size={24} strokeWidth={1.75} />
                    <span className="font-heading text-lg sm:text-xl uppercase tracking-tight">{r.title}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-[#C7CDD4] font-medium text-base leading-relaxed pb-6 pl-10">
                  {r.body?.map((p, idx) => <p key={idx} className="mb-3">{p}</p>)}
                  {r.list && (
                    <ul className="space-y-2 my-3">
                      {r.list.map((li) => (
                        <li key={li} className="flex items-start gap-2">
                          <span className="text-[#FF3B30] mt-1.5 h-1.5 w-1.5 bg-[#FF3B30] shrink-0" /> {li}
                        </li>
                      ))}
                    </ul>
                  )}
                  {r.after?.map((p, idx) => <p key={idx} className="mb-3">{p}</p>)}
                  {r.link && (
                    <a
                      href={r.link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="rules-schedule-pdf"
                      className="inline-flex items-center gap-2 mt-2 font-heading uppercase tracking-wide bg-[#FF3B30] text-white hover:bg-[#FF6B63] transition-colors px-5 py-3 text-sm"
                    >
                      <Download size={16} /> {r.link.label}
                    </a>
                  )}
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>

        {/* Vision */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-12 bg-[#141414] border border-white/10 p-8 sm:p-10"
          data-testid="vision-block"
        >
          <div className="flex items-center gap-3">
            <Heart className="text-[#FF3B30]" size={28} />
            <h3 className="text-2xl sm:text-3xl uppercase tracking-tight">Our Vision</h3>
          </div>
          <p className="mt-5 text-[#C7CDD4] font-medium leading-relaxed">
            We want this league to be competitive, organized, and fun while staying true to the values it was built on:
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            {VISION.map((v) => (
              <span key={v} className="bg-[#FF3B30]/10 border border-[#FF3B30]/40 text-white px-4 py-2 text-sm font-bold uppercase tracking-wide">
                {v}
              </span>
            ))}
          </div>
          <p className="mt-6 text-[#C7CDD4] font-medium leading-relaxed">
            We appreciate the overwhelming support we've received in just five short days and look forward to building
            something special together for years to come.
          </p>
          <p className="mt-4 text-white font-bold leading-relaxed">
            Please understand that the items outlined above are not up for debate or compromise. These are foundational
            league rules and values. If they do not align with what you're looking for, we sincerely appreciate your
            interest, but this may not be the league for you.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
